// src/ApiPanel/pages/ca/TdsReport.jsx
import React, { useState } from "react";
import {
  Card, DataTable, DateField, ExportButton, FilterBar, Notice, ReportPage,
  Stat, StatGrid, count, daysAgoISO, money, rupees, todayISO,
} from "../../components/reports/ReportKit";
import { useReportQuery } from "../../hooks/useReportQuery";
import { apiPanelDownload, qs } from "../../utils/apiPanelFetch";
import { API_PANEL_ENDPOINTS } from "../../constants/apiPanelAuth";

const defaults = () => ({ start_date: daysAgoISO(89), end_date: todayISO() });

export default function TdsReport() {
  const [filters, setFilters] = useState(defaults);
  const [applied, setApplied] = useState(defaults);
  const [exporting, setExporting] = useState(false);

  const { data, loading, error } = useReportQuery(API_PANEL_ENDPOINTS.CA_TDS, applied);

  const totals = data?.totals || {};
  const rows = data?.data || [];
  const quarters = data?.quarter_summary || [];

  const set = (key) => (value) => setFilters((f) => ({ ...f, [key]: value }));

  const handleExport = async () => {
    setExporting(true);
    try {
      await apiPanelDownload(
        `${API_PANEL_ENDPOINTS.CA_TDS}${qs({ ...applied, export: "csv" })}`,
        `tds_report_${applied.start_date}_${applied.end_date}.csv`
      );
    } catch {
      /* no download; report still on screen */
    } finally {
      setExporting(false);
    }
  };

  return (
    <ReportPage
      title="TDS"
      subtitle="Tax deducted at source on the commission you earned, by month and by financial-year quarter."
      actions={<ExportButton onExport={handleExport} busy={exporting} />}
    >
      <Card>
        <FilterBar
          onApply={() => setApplied(filters)}
          onReset={() => {
            const fresh = defaults();
            setFilters(fresh);
            setApplied(fresh);
          }}
          busy={loading}
        >
          <DateField label="From" value={filters.start_date} onChange={set("start_date")} max={filters.end_date} />
          <DateField label="To" value={filters.end_date} onChange={set("end_date")} min={filters.start_date} max={todayISO()} />
        </FilterBar>
      </Card>

      <StatGrid>
        <Stat label="Commission" value={rupees(totals.commission)} />
        <Stat label="TDS Deducted" value={rupees(totals.tds_deducted)} tone="warn" />
        <Stat label="Net Credited" value={rupees(totals.net_credited)} tone="ok" />
      </StatGrid>

      <Notice tone="warn">
        These are the deductions this platform made. Reconciling them against Form 26AS or a
        Form 16A needs the deductor&rsquo;s TAN and challan details, which this system does not
        hold — treat this as a working figure, not a tax certificate.
      </Notice>

      <Card title="Quarter summary" right={<span className="apip-hint">Indian FY quarters — Q1 is Apr–Jun</span>}>
        <DataTable
          columns={[
            { key: "quarter", label: "Quarter" },
            { key: "txn_count", label: "Txns", numeric: true, render: (r) => count(r.txn_count) },
            { key: "commission", label: "Commission", numeric: true, render: (r) => money(r.commission) },
            { key: "tds_deducted", label: "TDS", numeric: true, render: (r) => money(r.tds_deducted) },
            {
              key: "net_credited",
              label: "Net Credited",
              numeric: true,
              render: (r) => <strong>{money(r.net_credited)}</strong>,
            },
          ]}
          rows={quarters}
          loading={loading}
          empty="No TDS in this range."
          rowKey={(r) => r.quarter}
        />
      </Card>

      <Card title="Month-wise detail">
        <DataTable
          columns={[
            { key: "month_label", label: "Month" },
            { key: "financial_year", label: "FY" },
            { key: "quarter", label: "Quarter" },
            { key: "txn_count", label: "Txns", numeric: true, render: (r) => count(r.txn_count) },
            { key: "commission", label: "Commission", numeric: true, render: (r) => money(r.commission) },
            { key: "tds_deducted", label: "TDS", numeric: true, render: (r) => money(r.tds_deducted) },
            { key: "net_credited", label: "Net Credited", numeric: true, render: (r) => money(r.net_credited) },
            {
              key: "effective_rate_pct",
              label: "Effective Rate",
              numeric: true,
              render: (r) => `${Number(r.effective_rate_pct || 0).toFixed(2)}%`,
            },
          ]}
          rows={rows}
          loading={loading}
          error={error}
          empty="No TDS in this range."
          rowKey={(r) => r.month}
          footer={
            rows.length
              ? [
                  { value: "Totals" }, "", "",
                  "",
                  { value: money(totals.commission), numeric: true },
                  { value: money(totals.tds_deducted), numeric: true },
                  { value: money(totals.net_credited), numeric: true },
                  "",
                ]
              : null
          }
        />
      </Card>
    </ReportPage>
  );
}
